const groups = {
  length: { m: [1, 0], ft: [0.3048, 0], nm: [1852, 0] },
  speed: {
    "m/s": [1, 0],
    kn: [0.5144444444, 0],
    "km/h": [1 / 3.6, 0],
    mph: [0.44704, 0],
  },
  temperature: { "°C": [1, 0], "°F": [5 / 9, (-32 * 5) / 9], K: [1, -273.15] },
  angle: { deg: [Math.PI / 180, 0], rad: [1, 0] },
  pressure: {
    Pa: [1, 0],
    kPa: [1000, 0],
    bar: [100000, 0],
    psi: [6894.757293, 0],
    hPa: [100, 0],
    inHg: [3386.389, 0],
  },
  volume: { L: [1, 0], gal: [3.785411784, 0] },
  time: { s: [1, 0], min: [60, 0], h: [3600, 0] },
  percentage: { "%": [1, 0] },
  voltage: { V: [1, 0] },
  current: { A: [1, 0] },
  power: { W: [1, 0], kW: [1000, 0] },
  rotation: { rpm: [1, 0] },
  "angular-rate": { "deg/s": [1, 0], "deg/min": [1 / 60, 0] },
  salinity: { ppm: [1, 0] },
  frequency: { Hz: [1, 0] },
  signal: { dBm: [1, 0] },
};
export const supportedUnits = [
  "inherit",
  ...Object.values(groups).flatMap((g) => Object.keys(g)),
];
export function dimensionForUnit(unit) {
  return Object.keys(groups).find((k) => unit in groups[k]) ?? null;
}
export function convert(value, from, to) {
  if (from === to || !to || to === "inherit") return value;
  const dim = dimensionForUnit(from);
  if (!dim || dimensionForUnit(to) !== dim)
    throw new Error(`Incompatible units ${from} → ${to}`);
  const [a, b] = groups[dim][from],
    [c, d] = groups[dim][to];
  return (value * a + b - d) / c;
}
/**
 * A duration in seconds as an owner reads it (TOM-742, the cloud's
 * `formatDurationSeconds` and the iOS app's, to the character): at most two
 * units, the two largest, the second dropped at zero ("2 h 15 min", "10 d");
 * rounded to the smaller unit shown, then split (59.6 s is "1 min"); "0 s",
 * "<1 s" under a second, "−" before a negative, "—" for no number.
 */
export function formatDuration(seconds) {
  if (typeof seconds !== "number" || !Number.isFinite(seconds)) return "—";
  if (seconds < 0) return `\u2212${formatDuration(-seconds)}`;
  if (seconds === 0) return "0 s";
  if (seconds < 1) return "<1 s";
  const pair = (big, bigUnit, small, smallUnit) =>
    small === 0
      ? `${big} ${bigUnit}`
      : `${big} ${bigUnit} ${small} ${smallUnit}`;
  const s = Math.round(seconds);
  if (s < 60) return `${s} s`;
  if (s < 3600) return pair(Math.floor(s / 60), "min", s % 60, "s");
  const m = Math.round(seconds / 60);
  if (m < 1440) return pair(Math.floor(m / 60), "h", m % 60, "min");
  const h = Math.round(seconds / 3600);
  return pair(Math.floor(h / 24), "d", h % 24, "h");
}
export function formatReading(reading, options = {}) {
  if (
    !reading ||
    reading.value === null ||
    reading.value === undefined ||
    reading.quality === "missing" ||
    reading.quality === "disconnected"
  )
    return {
      value: "—",
      unit:
        options.unit === "inherit"
          ? (reading?.unit ?? "")
          : (options.unit ?? ""),
      quality: reading?.quality ?? "missing",
    };
  if (typeof reading.value !== "number")
    return {
      value: String(reading.value),
      unit: "",
      quality: reading.quality ?? "good",
    };
  if (!Number.isFinite(reading.value))
    return { value: "—", unit: "", quality: "missing" };
  let unit =
    options.unit && options.unit !== "inherit"
      ? options.unit
      : (reading.unit ?? "");
  let value = reading.value;
  if (unit !== reading.unit && reading.unit)
    value = convert(value, reading.unit, unit);
  // A duration reads in days, hours, minutes and seconds, never more than
  // 60 s (TOM-742); the reading's own number stays for a gauge or a bar. A
  // reading in hours stays in hours: an engine's hour meter reads "1,234 h".
  if (unit === "s" || unit === "min")
    return {
      value: formatDuration(convert(value, unit, "s")),
      unit: "",
      quality: reading.quality ?? "good",
    };
  const decimals =
    options.decimals && options.decimals !== "auto"
      ? Number(options.decimals)
      : Math.abs(value) >= 100
        ? 0
        : Math.abs(value) < 10
          ? 1
          : 1;
  const text = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    useGrouping: true,
    signDisplay: options.sign === "always" ? "always" : "auto",
  }).format(value);
  return {
    value: options.leadingZeros ? text.padStart(4, "0") : text,
    unit,
    quality: reading.quality ?? "good",
  };
}
export function angleDegrees(reading) {
  if (
    !reading ||
    ["missing", "disconnected"].includes(reading.quality) ||
    typeof reading.value !== "number" ||
    !Number.isFinite(reading.value)
  )
    return null;
  return reading.unit === "rad"
    ? convert(reading.value, "rad", "deg")
    : reading.value;
}
export function circularMean(values) {
  const valid = values.filter(Number.isFinite);
  if (!valid.length) return null;
  const x = valid.reduce((s, v) => s + Math.cos((v * Math.PI) / 180), 0),
    y = valid.reduce((s, v) => s + Math.sin((v * Math.PI) / 180), 0);
  if (Math.hypot(x, y) < 1e-10) return null;
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}
export function smoothSeries(points, mode = "off", circular = false) {
  if (mode === "off") return points;
  return points.map((p, i) => {
    if (p.value === null || (p.quality && p.quality !== "good")) return p;
    const list = points.slice(Math.max(0, i - 2), i + 1);
    if (
      list.some((x) => x.value === null || (x.quality && x.quality !== "good"))
    )
      return p;
    const values = list.map((x) => x.value);
    return {
      ...p,
      value: circular
        ? circularMean(values)
        : values.reduce((a, b) => a + b, 0) / values.length,
    };
  });
}
export function durationText(seconds, format = "auto") {
  if (!Number.isFinite(seconds)) return "—";
  const sign = seconds < 0 ? "−" : "",
    n = Math.abs(Math.floor(seconds)),
    h = Math.floor(n / 3600),
    m = Math.floor(n / 60) % 60,
    s = n % 60;
  return (
    sign +
    (format === "hh:mm:ss" || h ? `${String(h).padStart(2, "0")}:` : "") +
    `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
  );
}
